import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'

const API_BASE_URL = '/api/preventive-plans'

/**
 * Custom hook for managing preventive care plans
 */
const usePreventivePlans = () => {
  const [planTemplates, setPlanTemplates] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState('')

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('')
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])

  /**
   * Fetch all plan templates
   */
  const fetchPlanTemplates = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await axios.get(`${API_BASE_URL}/templates`)
      setPlanTemplates(response.data)
    } catch (err) {
      setError('Error fetching preventive plan templates')
      console.error('Error fetching preventive plan templates:', err)
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Fetch a single plan template by ID
   */
  const fetchPlanTemplateById = useCallback(async (templateId) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await axios.get(`${API_BASE_URL}/templates/${templateId}`)
      return response.data
    } catch (err) {
      setError(`Error fetching template with ID: ${templateId}`)
      console.error(`Error fetching template with ID: ${templateId}`, err)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Create a new plan template
   */
  const createPlanTemplate = useCallback(async (templateData) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await axios.post(`${API_BASE_URL}/templates`, templateData)
      setPlanTemplates((prev) => [...prev, response.data])
      setSuccessMessage('Preventive plan template created successfully')
      return response.data
    } catch (err) {
      setError('Error creating preventive plan template')
      console.error('Error creating preventive plan template:', err)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Update an existing plan template
   */
  const updatePlanTemplate = useCallback(async (templateId, templateData) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await axios.put(`${API_BASE_URL}/templates/${templateId}`, templateData)
      setPlanTemplates((prev) => prev.map((template) => (template.id === templateId ? response.data : template)))
      setSuccessMessage('Preventive plan template updated successfully')
      return response.data
    } catch (err) {
      setError(`Error updating template with ID: ${templateId}`)
      console.error(`Error updating template with ID: ${templateId}`, err)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Delete a plan template
   */
  const deletePlanTemplate = useCallback(async (templateId) => {
    setIsLoading(true)
    setError(null)

    try {
      await axios.delete(`${API_BASE_URL}/templates/${templateId}`)
      setPlanTemplates((prev) => prev.filter((template) => template.id !== templateId))
      setSuccessMessage('Preventive plan template deleted successfully')
      return true
    } catch (err) {
      setError(`Error deleting template with ID: ${templateId}`)
      console.error(`Error deleting template with ID: ${templateId}`, err)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Load plan templates on initial mount
  useEffect(() => {
    fetchPlanTemplates()
  }, [fetchPlanTemplates])

  return {
    planTemplates,
    isLoading,
    error,
    successMessage,
    fetchPlanTemplates,
    fetchPlanTemplateById,
    createPlanTemplate,
    updatePlanTemplate,
    deletePlanTemplate
  }
}

export default usePreventivePlans
